import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { CSGComponent } from './CSGComponent.js';

import type { CSGComponentOptions } from './CSGComponent.js';
import type { ActiveManifold, ManifoldWrapper } from '../../csg/ManifoldWrapper.js';


export interface CSGSphereComponentOptions extends CSGComponentOptions {
  /** The radius of the sphere */
  radius?: number;
  /** The number of segments (longitude and latitude) */
  segments?: number;
}

/** Sphere shape for CSG operations. */
@EngineClass('CSGSphereComponent')
export class CSGSphereComponent extends CSGComponent<CSGSphereComponentOptions> {

  @property({ category: 'CSG', type: 'number', min: 0.01, step: 0.1, decimals: 2, description: 'Radius of the sphere' })
  protected radius: number = 0.5;

  @property({ category: 'CSG', type: 'number', min: 3, max: 128, step: 1, decimals: 0, description: 'Number of segments' })
  protected segments: number = 32;

  public override initialize(options?: CSGSphereComponentOptions): void {
    super.initialize(options);
    this.radius = options?.radius ?? this.radius;
    this.segments = options?.segments ?? this.segments;
  }

  public override createManifold(manifoldWrapper: ManifoldWrapper, isRoot: boolean = false): ActiveManifold | null {
    if (!this.enabled || this._pendingRemoval) return null;

    const s = this.scale;
    const avgScale = (s.x + s.y + s.z) / 3;
    const manifold = manifoldWrapper.sphere(this.radius * avgScale, this.segments);
    manifold.setMaterial(this.material);

    if (!isRoot) {
      const deg = 180 / Math.PI;
      manifold.rotate(new THREE.Vector3(
        this.rotation.x * deg,
        this.rotation.y * deg,
        this.rotation.z * deg,
      ));
      manifold.translate(this.position);
    }

    return manifold;
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Sphere';
  }
}
